import React from "react";
import { useFormContext, FormFieldValue } from "./FormContext";

export interface WithFormValidationProps {
  name: string;
  value?: FormFieldValue;
  checked?: boolean;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onCheckboxChange?: (checked: boolean) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onSelectChange?: (value: FormFieldValue) => void;
  error?: boolean;
  [key: string]: unknown;
}

export const withFormValidation = <P extends WithFormValidationProps>(
  WrappedComponent: React.ComponentType<P>
) => {
  const WithFormValidation = (props: P) => {
    const { name, onSelectChange: externalOnSelectChange, ...restProps } = props;
    const { formData, validationResult, updateField, touchField, isSubmitted } =
      useFormContext();

    const value = formData[name] || "";
    const hasError = isSubmitted && validationResult.hasFieldError(name);

    const handleChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const target = event.target as HTMLInputElement;
      const newValue =
        target.type === "checkbox" ? target.checked : target.value;
      updateField(name, newValue);
    };

    const handleCheckboxChange = (checked: boolean) => {
      updateField(name, checked);
    };

    const handleBlur = () => {
      touchField(name);
    };

    const handleFocus = () => {
      touchField(name);
    };

    const handleSelectChange = (value: FormFieldValue) => {
      updateField(name, value);
      // Also call external handler if provided
      externalOnSelectChange?.(value);
    };

    return (
      <WrappedComponent
        {...(restProps as P)}
        name={name}
        value={value}
        checked={typeof value === "boolean" ? value : false}
        onChange={handleChange}
        onCheckboxChange={handleCheckboxChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onSelectChange={handleSelectChange}
        error={hasError}
      />
    );
  };

  WithFormValidation.displayName = `withFormValidation(${
    WrappedComponent.displayName || WrappedComponent.name
  })`;

  return WithFormValidation;
};
